import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import { fetchDashboardInit, fetchTodayVisits, fetchTodayAllVisits, generatePatientVisitSummary } from '../../services/doctorDashboardService';

const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:5002';
import ApiConfigFactory from '../../configurations/ApiConfigFactory';
import { configurePatientVisitForm } from '../../configurations/patientVisitFormConfig';
import FormModal from '../FormModal';
import LabTestRequestForm from '../Lab/LabTestRequestForm';
import LabTestReport from '../Lab/LabTestReport';
import LabTestRequestDetailForm from '../Lab/LabTestRequestDetailForm';
import DoctorNavPanel from './DoctorNavPanel';
import DoctorVisitsList from './DoctorVisitsList';
import DoctorContextPanel from './DoctorContextPanel';
import '../../styles/DoctorLandingPage.css';
import ReferralModal from './ReferralModal';
import UnreadMessagesModal from './UnreadMessagesModal';
import useConversations from '../../hooks/useConversations';

const REFRESH_INTERVAL = 60000;

const toDateInputValue = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const shiftDateValue = (dateValue, dayOffset) => {
    if (!dateValue) return toDateInputValue();
    const [year, month, day] = dateValue.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + dayOffset);
    return toDateInputValue(date);
};

const buildVisitApiConfig = (visit) => {
    const patient = visit?.Patient || {};
    const patientName = `${patient.patient_first_name || ''} ${patient.patient_last_name || ''}`.trim();

    const config = new ApiConfigFactory(
        'patients/visits',
        `Visit for Patient: ${patientName}`,
        'patient_id',
        patient.patient_id
    );

    config.formTitle = `Visit for Patient: ${patientName}`;
    config.allowAdd = false;
    config.path = '/patient-visits';

    configurePatientVisitForm(config, { patient, mode: 'doctor' });

    return config;
};

const DoctorLandingPage = () => {
    const { token, hasPermission, user } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [doctorProfile, setDoctorProfile] = useState(null);
    const [visits, setVisits] = useState([]);
    const [myLabRequests, setMyLabRequests] = useState([]);
    const [otherLabRequests, setOtherLabRequests] = useState([]);
    const [myRadiologyRequests, setMyRadiologyRequests] = useState([]);
    const [otherRadiologyRequests, setOtherRadiologyRequests] = useState([]);
    const [stats, setStats] = useState({});
    const [activeView, setActiveView] = useState('visits');
    const [showAllDoctors, setShowAllDoctors] = useState(() => user?.role === 'Admin');
    const [selectedDate, setSelectedDate] = useState(toDateInputValue());
    const [showFormModal, setShowFormModal] = useState(false);
    const [formModalVisit, setFormModalVisit] = useState(null);
    const [showLabRequestModal, setShowLabRequestModal] = useState(false);
    const [labRequestPatient, setLabRequestPatient] = useState(null);
    const [showLabReportModal, setShowLabReportModal] = useState(false);
    const [labReportId, setLabReportId] = useState(null);
    const [showRadiologyResultModal, setShowRadiologyResultModal] = useState(false);
    const [radiologyResultRequest, setRadiologyResultRequest] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showReferralModal, setShowReferralModal] = useState(false);
    const [referralVisit, setReferralVisit] = useState(null);
    const [summaryLoadingByPatient, setSummaryLoadingByPatient] = useState({});
    const [summaryErrorByPatient, setSummaryErrorByPatient] = useState({});
    const [unreadMessagesDismissed, setUnreadMessagesDismissed] = useState(false);
    const { messageUnreadCount, fetchUnreadCount } = useConversations();

    const refreshTimerRef = useRef(null);

    const loadDashboard = useCallback(async () => {
        if (!token) return;
        try {
            const data = await fetchDashboardInit(token, selectedDate);
            const viewerMode = !data.profile || data.viewAllDoctors;
            if (viewerMode && !showAllDoctors) {
                setShowAllDoctors(true);
            }
            const visitsData = (showAllDoctors || viewerMode)
                ? (viewerMode ? (data.visits || []) : await fetchTodayAllVisits(token, selectedDate))
                : (data.visits || []);
            setDoctorProfile(data.profile || null);
            setVisits(visitsData);
            setMyLabRequests(data.myLabRequests || data.labRequests || []);
            setOtherLabRequests(data.otherLabRequests || []);
            setMyRadiologyRequests(data.myRadiologyRequests || data.radiologyRequests || []);
            setOtherRadiologyRequests(data.otherRadiologyRequests || []);
            setStats(data.stats || {});
            setError(null);
            fetchUnreadCount().catch(console.error);
        } catch (err) {
            console.error('Dashboard load error:', err);
            if (err.response?.status === 404) {
                setError('Your user account is not linked to a doctor profile. Please contact IT.');
            } else {
                setError('Failed to load dashboard. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }, [token, showAllDoctors, selectedDate]);

    useEffect(() => {
        loadDashboard();
        refreshTimerRef.current = setInterval(loadDashboard, REFRESH_INTERVAL);
        return () => clearInterval(refreshTimerRef.current);
    }, [loadDashboard]);

    const handleOpenVisit = (visit) => {
        setFormModalVisit(visit);
        setShowFormModal(true);
    };

    const handleGenerateSummary = async (visit) => {
        if (!token) return;
        const patientId = visit?.Patient?.patient_id;
        if (!patientId) return;

        setSummaryLoadingByPatient(prev => ({ ...prev, [patientId]: true }));
        setSummaryErrorByPatient(prev => ({ ...prev, [patientId]: '' }));

        try {
            const result = await generatePatientVisitSummary(token, patientId);
            const summary = result?.data?.summary || '';
            const chronicDiagnoses = Array.isArray(result?.data?.chronic_diagnoses) ? result.data.chronic_diagnoses : [];
            const allergies = Array.isArray(result?.data?.allergies) ? result.data.allergies : [];

            setVisits(prev => prev.map(v => {
                const vPatientId = v?.Patient?.patient_id;
                if (vPatientId !== patientId) return v;
                return {
                    ...v,
                    patientSummary: {
                        status: 'ready',
                        summary,
                        chronic_diagnoses: chronicDiagnoses,
                        allergies,
                    },
                };
            }));
        } catch (err) {
            const message = err?.response?.data?.error || 'Failed to generate summary';
            setSummaryErrorByPatient(prev => ({ ...prev, [patientId]: message }));
        } finally {
            setSummaryLoadingByPatient(prev => ({ ...prev, [patientId]: false }));
        }
    };

    const handleManualRefresh = async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        await loadDashboard();
        setIsRefreshing(false);
    };

    const handleRequestLab = (visit) => {
        setLabRequestPatient(visit.Patient || null);
        setShowLabRequestModal(true);
    };

    const handleLabRequestClose = () => {
        setShowLabRequestModal(false);
        setLabRequestPatient(null);
        loadDashboard();
    };

    const handleViewReport = (requestId) => {
        setLabReportId(requestId);
        setShowLabReportModal(true);
    };

    const handleLabReportClose = () => {
        setShowLabReportModal(false);
        setLabReportId(null);
    };

    const handleEnterRadiologyResults = (request) => {
        setRadiologyResultRequest(request || null);
        setShowRadiologyResultModal(true);
    };

    const handleRadiologyResultClose = () => {
        setShowRadiologyResultModal(false);
        setRadiologyResultRequest(null);
        loadDashboard();
    };

    const handleToggleAllDoctors = () => {
        setShowAllDoctors(prev => !prev);
    };

    const handleSelectedDateChange = (nextDate) => {
        if (!nextDate) return;
        setSelectedDate(nextDate);
    };

    const handlePreviousDate = () => {
        setSelectedDate(prev => shiftDateValue(prev, -1));
    };

    const handleNextDate = () => {
        setSelectedDate(prev => shiftDateValue(prev, 1));
    };

    const handleTodayDate = () => {
        setSelectedDate(toDateInputValue());
    };

    const handleFormClose = async () => {
        setShowFormModal(false);
        setFormModalVisit(null);
        // Refresh visits after editing
        if (token) {
            try {
                const data = (showAllDoctors || !doctorProfile)
                    ? await fetchTodayAllVisits(token, selectedDate)
                    : await fetchTodayVisits(token, selectedDate);
                setVisits(data || []);
            } catch (err) {
                console.error('Error refreshing visits:', err);
            }
        }
    };

    const handleReferVisit = (visit) => {
        setReferralVisit(visit);
        setShowReferralModal(true);
    };

    const handleReferralClose = () => {
        setShowReferralModal(false);
        setReferralVisit(null);
    };

    const handleReferralSubmit = async (toDoctor, comment) => {
        await axios.post(
            `${BASE_URL}/hospital-portal/api/patients/referrals`,
            {
                patient_id: referralVisit.patient_id,
                referred_by_doctor: referralVisit.assigned_doctor,
                referred_to_doctor: toDoctor,
                notes: comment,
                branch: referralVisit.branch,
                source_visit_id: referralVisit.visit_id || null,
            },
            { headers: { Authorization: `Bearer ${token}` } }
        );

        handleReferralClose();
        loadDashboard();
    };

    const handleNavClick = (key, path) => {
        setActiveView(key);
        if (path) {
            navigate(path);
        }
    };

    if (loading) {
        return (
            <div className="doctor-landing-page">
                <div className="loading-state">Loading your dashboard...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="doctor-landing-page">
                <div className="error-state">
                    <p>{error}</p>
                    <button onClick={loadDashboard}>Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className="doctor-landing-page">
            <DoctorNavPanel
                activeView={activeView}
                onNavClick={handleNavClick}
                stats={stats}
            />

            <DoctorVisitsList
                visits={visits}
                doctorName={doctorProfile?.doctor_name || ''}
                onOpenVisit={handleOpenVisit}
                onRequestLab={handleRequestLab}
                onReferVisit={handleReferVisit}
                onGenerateSummary={handleGenerateSummary}
                showAllDoctors={showAllDoctors}
                onToggleAllDoctors={handleToggleAllDoctors}
                canToggleAllDoctors={Boolean(doctorProfile)}
                selectedDate={selectedDate}
                onSelectedDateChange={handleSelectedDateChange}
                onPreviousDate={handlePreviousDate}
                onNextDate={handleNextDate}
                onTodayDate={handleTodayDate}
                onRefresh={handleManualRefresh}
                isRefreshing={isRefreshing}
                canExecuteSummary={hasPermission ? hasPermission('/ai-assist/patient-visit-summary', 'Execute') : true}
                summaryLoadingByPatient={summaryLoadingByPatient}
                summaryErrorByPatient={summaryErrorByPatient}
            />

            {!showFormModal && (
                <DoctorContextPanel
                    myLabRequests={myLabRequests}
                    otherLabRequests={otherLabRequests}
                    myRadiologyRequests={myRadiologyRequests}
                    otherRadiologyRequests={otherRadiologyRequests}
                    onViewReport={handleViewReport}
                    onEnterRadiologyResults={handleEnterRadiologyResults}
                />
            )}

            {showFormModal && formModalVisit && (
                <div className="data-list">
                    <FormModal
                        initialData={formModalVisit}
                        onClose={handleFormClose}
                        apiConfig={buildVisitApiConfig(formModalVisit)}
                    />
                </div>
            )}

            {showLabRequestModal && labRequestPatient && (
                <div className="lab-request-modal-overlay">
                    <div className="lab-request-modal-content lab-request-modal-content--wide">
                        <LabTestRequestForm
                            patient={labRequestPatient}
                            onClose={handleLabRequestClose}
                        />
                    </div>
                </div>
            )}

            {showLabReportModal && labReportId && (
                <div className="lab-request-modal-overlay">
                    <div className="lab-request-modal-content">
                        <LabTestReport
                            reportId={labReportId}
                            onClose={handleLabReportClose}
                        />
                    </div>
                </div>
            )}

            {showRadiologyResultModal && radiologyResultRequest && (
                <div className="lab-request-modal-overlay">
                    <div className="lab-request-modal-content lab-request-modal-content--wide">
                        <LabTestRequestDetailForm
                            request={radiologyResultRequest}
                            requestId={radiologyResultRequest.id}
                            onClose={handleRadiologyResultClose}
                        />
                    </div>
                </div>
            )}

            {showReferralModal && referralVisit && (
                <ReferralModal
                    visit={referralVisit}
                    referringDoctorName={doctorProfile?.doctor_name || ''}
                    onClose={handleReferralClose}
                    onSubmit={handleReferralSubmit}
                    token={token}
                />
            )}

            {!loading && !unreadMessagesDismissed && messageUnreadCount > 0 && (
                <UnreadMessagesModal
                    unreadCount={messageUnreadCount}
                    onDismiss={() => setUnreadMessagesDismissed(true)}
                />
            )}
        </div>
    );
};

export default DoctorLandingPage;
